import React, {useEffect, useRef, useState} from 'react'
import './Casting.css';

import Header from '../../sections/Header/Header';
import Footer from '../../sections/Footer/Footer';
import SEO from '../../components/SEO/SEO';

import { submitCasting, uploadCastingPhoto } from '../../../services/castingService';

const initialForm = { name:'', email:'', phone:'', whatsapp:'', telegram:'', age:'', city:'', nationality:'', experience:'', about:'' };
const maxPhotoSize = 10 * 1024 * 1024;
const allowedPhotoTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif']);

const Casting = () => {

    const [form, setForm] = useState(initialForm)
    const [submitted, setSubmitted] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submissionStep, setSubmissionStep] = useState('')
    const [error, setError] = useState('')
    const [photos, setPhotos] = useState([])
    const [lightbox, setLightbox] = useState(null)
    const photosRef = useRef([])

    useEffect(() => {
        photosRef.current = photos
    }, [photos])

    useEffect(() => () => {
        photosRef.current.forEach(photo => URL.revokeObjectURL(photo.previewUrl))
    }, [])

    const handleChange = (field, value) => {
        setForm(prev => ({...prev, [field]: value}))
        if (error) setError('')
    }

    const handlePhotoChange = (event) => {
        const files = Array.from(event.target.files || [])
        const invalidFile = files.find(file => !allowedPhotoTypes.has(file.type) || file.size > maxPhotoSize)
        if (invalidFile) {
            setError('Photos must be JPEG, PNG, WebP, GIF, or AVIF files smaller than 10 MB.')
        } else {
            setError('')
            setPhotos(prev => [...prev, ...files.map(file => ({file, previewUrl: URL.createObjectURL(file)}))])
        }
        event.target.value = ''
    }

    const removePhoto = (index) => {
        setPhotos(prev => {
            const photo = prev[index]
            if (photo) URL.revokeObjectURL(photo.previewUrl)
            return prev.filter((_, photoIndex) => photoIndex !== index)
        })
        setLightbox(null)
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const missing = []
        if (!form.name.trim()) missing.push('your full name')
        if (!form.email.trim()) missing.push('an email address')
        if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) missing.push('a valid email address')

        if (missing.length) {
            setError(`Please provide ${missing.join(', ')}.`)
            return
        }

        setSubmitting(true)
        setError('')
        try {
            const photoUrls = []
            for (const photo of photos) {
                setSubmissionStep(`Uploading photo ${photoUrls.length + 1} of ${photos.length}...`)
                photoUrls.push(await uploadCastingPhoto(photo.file))
            }

            setSubmissionStep('Submitting application...')
            await submitCasting({
                ...form,
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                whatsapp: form.whatsapp.trim(),
                telegram: form.telegram.trim(),
                about: form.about.trim(),
                photos: photoUrls
            })
            setSubmitted(true)
        } catch (submissionError) {
            setError(submissionError instanceof TypeError
                ? 'Unable to connect to the casting service. Please try again.'
                : submissionError.message || 'We could not submit your application. Please try again.')
        } finally {
            setSubmitting(false)
            setSubmissionStep('')
        }
    }

    return (
        <>
            <SEO title="Casting • Join Our Escort Agency" description="Apply to join SADIDA Network, Italy's premier adult entertainment agency. Earn €500-€1,000/day working in Rome, Milan, and across Italy." />
            <Header />
            <section className="section-header casting-page">
                <div className="view-container">
                    <h1>Casting</h1>
                    <p className="casting-intro">
                        We're always looking for new talent to join the SADIDA Network. If you're confident,
                        professional, and ready to work with Italy's premier adult entertainment agencies,
                        we'd love to hear from you. Our talent typically earns from €500 - €1,000 per day.
                        Fill out the form below and our team will be in touch.
                    </p>

                    {submitted ? (
                        <div className="casting-success" role="status" aria-live="polite">
                            <p>Thank you for your application. Our team will review your submission and get back to you shortly.</p>
                        </div>
                    ) : (
                        <form className="casting-form" onSubmit={handleSubmit} noValidate>
                            {error && <p className="casting-error" role="alert">{error}</p>}

                            <div className="casting-field">
                                <label htmlFor="casting-name">Full Name</label>
                                <input id="casting-name" name="name" type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} autoComplete="name" required />
                            </div>
                            <div className="casting-field">
                                <label htmlFor="casting-email">Email</label>
                                <input id="casting-email" name="email" type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} autoComplete="email" required />
                            </div>
                            <div className="casting-field">
                                <label htmlFor="casting-phone">Phone</label>
                                <input id="casting-phone" name="phone" type="tel" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+39" autoComplete="tel" />
                            </div>
                            <div className="casting-row">
                                <div className="casting-field">
                                    <label htmlFor="casting-whatsapp">WhatsApp</label>
                                    <input id="casting-whatsapp" name="whatsapp" type="tel" value={form.whatsapp} onChange={(e) => handleChange('whatsapp', e.target.value)} placeholder="+39" />
                                </div>
                                <div className="casting-field">
                                    <label htmlFor="casting-telegram">Telegram</label>
                                    <input id="casting-telegram" name="telegram" type="text" value={form.telegram} onChange={(e) => handleChange('telegram', e.target.value)} placeholder="@username" />
                                </div>
                            </div>
                            <div className="casting-row">
                                <div className="casting-field">
                                    <label htmlFor="casting-age">Age</label>
                                    <select id="casting-age" name="age" value={form.age} onChange={(e) => handleChange('age', e.target.value)}>
                                        <option value="">Select</option>
                                        {Array.from({length:43}, (_, i) => 18 + i).map(a => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                </div>
                                <div className="casting-field">
                                    <label htmlFor="casting-city">City</label>
                                    <select id="casting-city" name="city" value={form.city} onChange={(e) => handleChange('city', e.target.value)}>
                                        <option value="">Select</option>
                                        {['Rome','Milan','Florence','Naples','Venice','Turin','Bologna','Palermo','Genoa','Verona'].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="casting-field">
                                <label htmlFor="casting-nationality">Nationality</label>
                                <select id="casting-nationality" name="nationality" value={form.nationality} onChange={(e) => handleChange('nationality', e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="Italy">Italy</option>
                                    <option disabled>──────────</option>
                                    {['Albania','Argentina','Austria','Belgium','Bosnia','Brazil','Bulgaria','Canada','China','Colombia','Croatia','Czech Republic','Denmark','Dominican Republic','Ecuador','Egypt','Estonia','Finland','France','Germany','Greece','Hungary','India','Ireland','Japan','Latvia','Lithuania','Mexico','Moldova','Montenegro','Morocco','Netherlands','Nigeria','Norway','Peru','Philippines','Poland','Portugal','Romania','Russia','Serbia','Slovakia','Slovenia','Spain','Sweden','Switzerland','Thailand','Tunisia','Turkey','UK','Ukraine','USA','Venezuela'].map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="casting-field">
                                <label htmlFor="casting-experience">Previous Experience</label>
                                <select id="casting-experience" name="experience" value={form.experience} onChange={(e) => handleChange('experience', e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="none">None</option>
                                    <option value="1-2">1-2 years</option>
                                    <option value="3-5">3-5 years</option>
                                    <option value="5+">5+ years</option>
                                </select>
                            </div>
                            <div className="casting-field">
                                <label htmlFor="casting-about">Tell us about yourself</label>
                                <textarea id="casting-about" name="about" value={form.about} onChange={(e) => handleChange('about', e.target.value)} rows="4" />
                            </div>
                            <div className="casting-field">
                                <label className="casting-label" htmlFor="casting-upload">Photos</label>
                                <div className="casting-photos">
                                    {photos.map((photo, index) => (
                                        <div className="casting-photo" key={`${photo.file.name}-${photo.file.lastModified}-${index}`}>
                                            <img src={photo.previewUrl} alt={`${photo.file.name} preview`} onClick={() => setLightbox(photo.previewUrl)} />
                                            <button type="button" className="casting-photo-delete" onClick={() => removePhoto(index)} aria-label={`Remove ${photo.file.name}`}>&times;</button>
                                        </div>
                                    ))}
                                    <label className="casting-photo-add" htmlFor="casting-upload"> <span aria-hidden="true">+</span><span className="casting-photo-add-label">Add photos</span></label>
                                    <input type="file" id="casting-upload" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" multiple onChange={handlePhotoChange} />
                                </div>
                                <p className="casting-help">JPEG, PNG, WebP, GIF, or AVIF. Maximum 10 MB per photo.</p>
                            </div>
                            <button className="casting-submit" type="submit" disabled={submitting}>
                                {submitting ? (submissionStep || 'Submitting...') : 'Submit Application'}
                            </button>
                        </form>
                    )}
                </div>
            </section>
            {lightbox && (
                <div className="lightbox-overlay" onClick={() => setLightbox(null)} role="presentation">
                    <button type="button" className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close photo preview">&times;</button>
                    <img src={lightbox} alt="Selected profile preview" className="lightbox-img" onClick={(event) => event.stopPropagation()} />
                </div>
            )}
            <Footer />
        </>
    )
}

export default Casting
